import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as contentApi from '@/api/content';
import type {
  ContentAdminListParams,
  ContentInput,
  ContentListParams,
  ContentSeedLink,
  RegisterAssetInput,
  UploadURLInput,
} from '@/types/content';

// SPEC-021: TanStack Query hooks for the content management API.
// Query keys are organized as ['content', <scope>, ...] so invalidating
// ['content'] after a mutation refreshes everything at once.

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useMyContent(params?: ContentListParams) {
  return useQuery({
    queryKey: ['content', 'mine', params],
    queryFn: () => contentApi.listMyContent(params),
    placeholderData: (prev) => prev,
  });
}

export function useContentDetail(id: string | undefined) {
  return useQuery({
    queryKey: ['content', 'detail', id],
    queryFn: () => contentApi.getContent(id as string),
    enabled: !!id,
  });
}

export function useAdminContent(params?: ContentAdminListParams) {
  return useQuery({
    queryKey: ['content', 'admin', params],
    queryFn: () => contentApi.adminListContent(params),
    placeholderData: (prev) => prev,
  });
}

export function useAdminPendingContent(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['content', 'admin', 'pending', params],
    queryFn: () => contentApi.adminPendingContent(params),
    placeholderData: (prev) => prev,
  });
}

export function useAdminTrashContent(params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['content', 'admin', 'trash', params],
    queryFn: () => contentApi.adminTrash(params),
    placeholderData: (prev) => prev,
  });
}

export function useContentModerationLog(id: string | undefined) {
  return useQuery({
    queryKey: ['content', 'admin', 'log', id],
    queryFn: () => contentApi.adminModerationLog(id as string),
    enabled: !!id,
  });
}

export function useContentMetrics(id: string | undefined) {
  return useQuery({
    queryKey: ['content', 'metrics', id],
    queryFn: () => contentApi.getContentMetrics(id as string),
    enabled: !!id,
  });
}

export function useContentTimeseries(
  id: string | undefined,
  range: '7d' | '30d' | '90d' = '30d',
) {
  return useQuery({
    queryKey: ['content', 'timeseries', id, range],
    queryFn: () => contentApi.getContentTimeseries(id as string, range),
    enabled: !!id,
  });
}

export function useContentDropoff(id: string | undefined) {
  return useQuery({
    queryKey: ['content', 'dropoff', id],
    queryFn: () => contentApi.getContentDropoff(id as string),
    enabled: !!id,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ContentInput) => contentApi.createContent(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useUpdateContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ContentInput }) =>
      contentApi.updateContent(id, input),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['content'] });
      qc.invalidateQueries({ queryKey: ['content', 'detail', id] });
    },
  });
}

export function useSubmitContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentApi.submitContent(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['content'] });
      qc.invalidateQueries({ queryKey: ['content', 'detail', id] });
    },
  });
}

export function useDeleteContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentApi.deleteContent(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useSetSeedLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      links,
    }: {
      id: string;
      links: Array<Pick<ContentSeedLink, 'seed_id' | 'link_type'>>;
    }) => contentApi.setSeedLinks(id, links),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['content', 'detail', id] });
    },
  });
}

// Media upload — the hook triggers the full three-step flow (request URL,
// PUT bytes, register asset) so callers only need a File + content id.
export function useUploadContentMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      contentId: string;
      file: File;
      assetType: 'image' | 'audio' | 'file';
      width?: number;
      height?: number;
      durationSec?: number;
    }) => {
      const urlInput: UploadURLInput = {
        asset_type: args.assetType,
        mime_type: args.file.type || 'application/octet-stream',
        filename: args.file.name,
      };
      const presigned = await contentApi.getUploadURL(args.contentId, urlInput);
      await contentApi.uploadToPresignedURL(presigned.upload_url, args.file);
      const registerInput: RegisterAssetInput = {
        asset_id: presigned.asset_id,
        asset_type: args.assetType,
        s3_object_key: presigned.s3_object_key,
        mime_type: args.file.type || 'application/octet-stream',
        size_bytes: args.file.size,
        width: args.width ?? null,
        height: args.height ?? null,
        duration_sec: args.durationSec ?? null,
      };
      return contentApi.registerAsset(args.contentId, registerInput);
    },
    onSuccess: (_data, { contentId }) => {
      qc.invalidateQueries({ queryKey: ['content', 'detail', contentId] });
    },
  });
}

export function useDeleteContentMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ contentId, assetId }: { contentId: string; assetId: string }) =>
      contentApi.deleteAsset(contentId, assetId),
    onSuccess: (_data, { contentId }) => {
      qc.invalidateQueries({ queryKey: ['content', 'detail', contentId] });
    },
  });
}

// ---------------------------------------------------------------------------
// Admin mutations
// ---------------------------------------------------------------------------

export function useApproveContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentApi.adminApprove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useRejectContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      contentApi.adminReject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useUnpublishContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentApi.adminUnpublish(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useArchiveContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentApi.adminArchive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useRestoreContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contentApi.adminRestore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}

export function useBulkContentAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      ids,
      action,
      reason,
    }: {
      ids: string[];
      action: 'approve' | 'reject' | 'unpublish' | 'archive';
      reason?: string;
    }) => contentApi.adminBulkAction(ids, action, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content'] });
    },
  });
}
